import { ActionItem, MapData, CuratedCheckData } from '../types';
import { decodeHtmlEntities } from '../utils';
import {
  MOBILE_ICON_SVG,
  DESKTOP_ICON_SVG,
  AREA_BADGES,
  EFFORT_LABELS
} from '../constants';
import { getConfidenceDisplay, getEffortDisplay } from '../utils';
import { SEMANTIC_COLORS } from '../design-system/colors';
import { CardActionsComponent } from './card-actions.component';

/**
 * Component for creating recommendation cards
 */
export class CardComponent {
  private cardActionsComponent: CardActionsComponent;

  constructor(cardActionsComponent?: CardActionsComponent) {
    this.cardActionsComponent = cardActionsComponent || new CardActionsComponent();
  }

  /**
   * Get the card actions component instance
   * @returns Card actions component
   */
  getCardActionsComponent(): CardActionsComponent {
    return this.cardActionsComponent;
  }

  /**
   * Create a card element for an action item
   * @param item - Action item to display
   * @param isTopICE - Whether this is a top ICE score item
   * @param mapData - Curated content map data
   * @returns Card HTML element
   */
  create(item: ActionItem, isTopICE: boolean, mapData: MapData | null): HTMLElement {
    const {
      area,
      checkKey,
      recommendation,
      problem,
      solutionDetails,
      sortLift,
      confidenceRank,
      iceScore
    } = item;

    const {
      idea = '',
      example = '',
      expectedLiftPct = 'N/A',
      requiredSample = 0,
      actionType = 'fix_now',
      effort = 3
    } = solutionDetails;

    // Get curated data
    const curatedData = this.findCuratedData(checkKey, area, mapData);
    const hasReferences = this.hasValidReferences(curatedData);
    const hasImages = this.hasValidImages(curatedData);
    const hasCuratedData = !!(curatedData && (hasReferences || hasImages));

    // Get display values
    const confidence = getConfidenceDisplay(confidenceRank);
    const effortDisplay = getEffortDisplay(effort);
    const badge = AREA_BADGES[area] || 'bg-gray-700';

    // Three-way action classification
    const getActionTypeDisplay = () => {
      switch (actionType) {
        case 'fix_now':
          return {
            label: 'Quick Win',
            bg: SEMANTIC_COLORS.implementation.apply.background,
            text: SEMANTIC_COLORS.implementation.apply.text
          };
        case 'test_to_quantify':
          return {
            label: 'Test Recommended',
            bg: SEMANTIC_COLORS.implementation.test.background,
            text: SEMANTIC_COLORS.implementation.test.text
          };
        case 'defer':
          return {
            label: 'Consider Later',
            bg: 'bg-gray-700/50 border-gray-500/50',
            text: 'text-gray-400'
          };
        default:
          return {
            label: 'Quick Win',
            bg: SEMANTIC_COLORS.implementation.apply.background,
            text: SEMANTIC_COLORS.implementation.apply.text
          };
      }
    };
    const actionDisplay = getActionTypeDisplay();
    const impl = actionDisplay.label;
    const implBg = actionDisplay.bg;
    const implClr = actionDisplay.text;

    // Create card element
    const card = document.createElement('div');
    const borderClass = isTopICE ? SEMANTIC_COLORS.topICE.border : 'border-gray-700';
    card.className = `bg-gray-800/50 border ${borderClass} rounded-lg overflow-hidden transition-all hover:border-gray-600 relative`;

    // Set data attributes
    Object.assign(card.dataset, {
      area,
      confidenceRank: confidenceRank.toString(),
      effort: effort.toString(),
      lift: sortLift.toString(),
      recommendation,
      iceScore: iceScore.toString(),
      deviceType: item.deviceType
    });

    // Build HTML
    card.innerHTML = this.buildCardHTML(
      item,
      isTopICE,
      badge,
      area,
      checkKey,
      confidence,
      implBg,
      impl,
      implClr,
      effortDisplay,
      expectedLiftPct,
      actionType,
      requiredSample,
      effort,
      hasCuratedData,
      curatedData,
      hasReferences,
      hasImages
    );

    // Populate text content
    this.populateTextContent(card, item, problem, idea, example);

    return card;
  }

  /**
   * Build the card HTML structure
   */
  private buildCardHTML(
    item: ActionItem,
    isTopICE: boolean,
    badge: string,
    area: string,
    checkKey: string,
    confidence: { text: string; color: string },
    implBg: string,
    impl: string,
    implClr: string,
    effortDisplay: { text: string; color: string },
    expectedLiftPct: string,
    actionType: 'fix_now' | 'test_to_quantify' | 'defer',
    requiredSample: number | string,
    effort: number,
    hasCuratedData: boolean,
    curatedData: CuratedCheckData | null,
    hasReferences: boolean,
    hasImages: boolean
  ): string {
    const checkTitle = this.formatCheckTitle(checkKey);

    // Generate card actions HTML
    const cardActionsHTML = this.cardActionsComponent.render(checkKey, item.deviceType);

    return `
      ${isTopICE ? `<div class="absolute top-2 right-2 z-10"><svg class="w-6 h-6 ${SEMANTIC_COLORS.topICE.icon} fill-current" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></div>` : ''}
      <div class="accordion-toggle w-full p-5 text-left">
        <div class="flex justify-between items-center gap-4 cursor-pointer" data-toggle="true">
          <div class="flex-1">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
              <span class="text-xs font-semibold px-2 py-1 rounded-full ${badge}">${area}</span>
              <span class="text-xs font-semibold px-2 py-1 rounded-full bg-gray-700/60 text-gray-300">${checkTitle}</span>
              ${this.getDeviceBadge(item.deviceType)}
              <span class="text-xs font-semibold ${confidence.color} ml-auto">Confidence: ${confidence.text}</span>
              <span class="text-xs font-semibold px-2 py-1 rounded-full ${implBg} border ${implClr}">${impl}</span>
              <span class="text-xs font-semibold ${effortDisplay.color}">Effort: ${effortDisplay.text}</span>
            </div>
          </div>
          <div class="text-right">
            <div class="text-xl font-bold ${SEMANTIC_COLORS.topICE.lift}">${expectedLiftPct}</div>
            <div class="text-xs text-gray-400">Expected Lift</div>
          </div>
          <svg class="chevron-icon w-6 h-6 text-gray-500 transition-transform duration-300 flex-shrink-0"
               xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <p class="recommendation-text text-sm md:text-lg font-semibold text-gray-100 break-words mt-2"></p>
        ${cardActionsHTML}
      </div>
      <div class="accordion-content">
        <div class="px-5 pb-5 pt-2 border-t border-gray-700/50">
          ${item.deviceType === 'both' ? this.buildDeviceSpecificSection() : this.buildSingleDeviceSection(actionType)}
          ${item.deviceType === 'both' ? this.buildCombinedMetrics() : this.buildSingleMetrics(expectedLiftPct, actionType, requiredSample, effortDisplay.text, effort)}
          ${this.buildCuratedSection(hasCuratedData, curatedData, hasReferences, hasImages, checkKey)}
        </div>
      </div>
    `;
  }

  /**
   * Get device badge HTML
   */
  private getDeviceBadge(deviceType: string): string {
    if (deviceType === 'both') {
      return `<span class="text-xs font-semibold px-2 py-1 rounded-full ${SEMANTIC_COLORS.device.both.background} border ${SEMANTIC_COLORS.device.both.text} flex items-center gap-1">${MOBILE_ICON_SVG} ${DESKTOP_ICON_SVG}</span>`;
    } else if (deviceType === 'mobile') {
      return `<span class="text-xs font-semibold px-2 py-1 rounded-full ${SEMANTIC_COLORS.device.mobile.background} border ${SEMANTIC_COLORS.device.mobile.text} flex items-center gap-1">${MOBILE_ICON_SVG}</span>`;
    } else {
      return `<span class="text-xs font-semibold px-2 py-1 rounded-full ${SEMANTIC_COLORS.device.desktop.background} border ${SEMANTIC_COLORS.device.desktop.text} flex items-center gap-1">${DESKTOP_ICON_SVG}</span>`;
    }
  }

  /**
   * Build device-specific recommendations section
   */
  private buildDeviceSpecificSection(): string {
    return `
      <div class="space-y-4">
        <h4 class="font-bold text-gray-300 mb-3 flex items-center gap-2">Device-Specific Recommendations</h4>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Mobile Section -->
          <div class="${SEMANTIC_COLORS.mobileSection.background} border rounded-lg p-4">
            <h5 class="font-semibold ${SEMANTIC_COLORS.mobileSection.title} mb-3 flex items-center gap-1 text-base">${MOBILE_ICON_SVG} Mobile</h5>
            <div class="space-y-3 text-sm">
              <div>
                <span class="font-semibold text-gray-300">Problem:</span>
                <p class="text-gray-400 mt-1 mobile-problem-text"></p>
              </div>
              <div>
                <span class="font-semibold text-gray-300 mobile-solution-label"></span>
                <p class="text-gray-400 mt-1 mobile-idea-text"></p>
              </div>
              <div class="mobile-example-section">
                <span class="font-semibold text-gray-300">Potential Solution:</span>
                <p class="text-gray-400 mt-1 mobile-example-text"></p>
              </div>
            </div>
          </div>

          <!-- Desktop Section -->
          <div class="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
            <h5 class="font-semibold text-cyan-400 mb-3 flex items-center gap-1 text-base">${DESKTOP_ICON_SVG} Desktop</h5>
            <div class="space-y-3 text-sm">
              <div>
                <span class="font-semibold text-gray-300">Problem:</span>
                <p class="text-gray-400 mt-1 desktop-problem-text"></p>
              </div>
              <div>
                <span class="font-semibold text-gray-300 desktop-solution-label"></span>
                <p class="text-gray-400 mt-1 desktop-idea-text"></p>
              </div>
              <div class="desktop-example-section">
                <span class="font-semibold text-gray-300">Potential Solution:</span>
                <p class="text-gray-400 mt-1 desktop-example-text"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Build single device section
   */
  private buildSingleDeviceSection(actionType: 'fix_now' | 'test_to_quantify' | 'defer'): string {
    const getSolutionLabel = () => {
      switch (actionType) {
        case 'test_to_quantify': return 'Test Idea';
        case 'defer': return 'Consideration';
        default: return 'Implementation Idea';
      }
    };
    return `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
        <div>
          <h4 class="font-bold text-gray-300 mb-1">Problem</h4>
          <p class="problem-text text-gray-400"></p>
        </div>
        <div>
          <h4 class="font-bold text-gray-300 mb-1">${getSolutionLabel()}</h4>
          <p class="idea-text text-gray-400"></p>
        </div>
        <div class="example-container">
          <h4 class="font-bold text-gray-300 mb-1">Potential Solution</h4>
          <p class="example-text text-gray-400"></p>
        </div>
      </div>
    `;
  }

  /**
   * Build combined metrics section for both devices
   */
  private buildCombinedMetrics(): string {
    return `
      <div class="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-700/50">
        <div>
          <h4 class="font-bold text-gray-300 mb-1">Expected Lift</h4>
          <p class="text-gray-400 combined-lift-text"></p>
        </div>
        <div class="combined-effort-section">
          <h4 class="font-bold text-gray-300 mb-1 combined-effort-label"></h4>
          <p class="text-gray-400 combined-effort-text"></p>
        </div>
      </div>
    `;
  }

  /**
   * Build single device metrics
   */
  private buildSingleMetrics(
    expectedLiftPct: string,
    actionType: 'fix_now' | 'test_to_quantify' | 'defer',
    requiredSample: number | string,
    effortText: string,
    effort: number
  ): string {
    const getSecondMetric = () => {
      switch (actionType) {
        case 'test_to_quantify':
          return `<div><h4 class="font-bold text-gray-300 mb-1">Required Sample</h4>
             <p class="text-gray-400">${this.formatSample(requiredSample)}</p></div>`;
        case 'defer':
          return `<div><h4 class="font-bold text-gray-300 mb-1">Priority</h4>
             <p class="text-gray-400">Low - revisit when resources allow</p></div>`;
        default: // fix_now
          return `<div><h4 class="font-bold text-gray-300 mb-1">Effort</h4>
             <p class="text-gray-400">${effortText} (${effort}/5)</p></div>`;
      }
    };
    return `
      <div class="grid grid-cols-2 gap-4 mt-6">
        <div>
          <h4 class="font-bold text-gray-300 mb-1">Expected Lift</h4>
          <p class="text-gray-400">${expectedLiftPct}</p>
        </div>
        ${getSecondMetric()}
      </div>
    `;
  }

  /**
   * Build curated content section
   */
  private buildCuratedSection(
    hasCuratedData: boolean,
    curatedData: CuratedCheckData | null,
    hasReferences: boolean,
    hasImages: boolean,
    checkKey: string
  ): string {
    if (!hasCuratedData) {
      return `
        <div class="mt-6">
          <div class="text-center py-4 text-gray-500 text-sm bg-gray-800/30 rounded-lg border border-gray-700/50">
            <svg class="w-8 h-8 mx-auto mb-2 opacity-50" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
            No curated content available yet
          </div>
        </div>
      `;
    }

    return `
      <div class="mt-6">
        <div class="grid grid-cols-1 ${hasReferences && hasImages ? 'lg:grid-cols-2' : ''} gap-6">
          <div>
            <h4 class="font-bold text-gray-300 mb-2">Curated References</h4>
            ${this.buildReferencesHTML(hasReferences, curatedData)}
          </div>
          <div>
            <h4 class="font-bold text-gray-300 mb-3">Sample Images</h4>
            ${this.buildImagesHTML(hasImages, curatedData, checkKey)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Build references HTML
   */
  private buildReferencesHTML(hasReferences: boolean, curatedData: CuratedCheckData | null): string {
    if (!hasReferences || !curatedData?.references) {
      return `
        <div class="text-center py-3 text-gray-500 text-sm bg-gray-800/20 rounded border border-gray-700/30">
          <svg class="w-5 h-5 mx-auto mb-1 opacity-50" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
          </svg>
          No curated references yet
        </div>
      `;
    }

    const validRefs = curatedData.references.filter(ref => ref.url && ref.url.trim() && ref.label && ref.label.trim());
    return `
      <ul class="space-y-1">
        ${validRefs.map(ref => `
          <li>
            <a href="${ref.url}" target="_blank" rel="noopener noreferrer"
               class="${SEMANTIC_COLORS.link} transition-colors text-sm flex items-center gap-1">
              <svg class="w-3 h-3 flex-shrink-0" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
              ${ref.label}
            </a>
          </li>
        `).join('')}
      </ul>
    `;
  }

  /**
   * Build images HTML
   */
  private buildImagesHTML(hasImages: boolean, curatedData: CuratedCheckData | null, checkKey: string): string {
    if (!hasImages || !curatedData?.images) {
      return `
        <div class="text-center py-3 text-gray-500 text-sm bg-gray-800/20 rounded border border-gray-700/30">
          <svg class="w-5 h-5 mx-auto mb-1 opacity-50" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          No curated examples yet
        </div>
      `;
    }

    const validImages = curatedData.images.filter(img => img.src && img.src.trim());
    return `
      <div class="flex flex-wrap gap-2">
        ${validImages.map(img => {
          const imgSrc = img.src.startsWith('http') ? img.src : './' + img.src;
          const alt = img.alt || checkKey + ' example';
          return `
            <button
              class="image-thumbnail relative group overflow-hidden rounded-md border border-gray-600 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm hover:shadow-md"
              data-src="${img.src}"
              data-alt="${alt}"
              onclick="window.openLightbox('${img.src.replace(/['"]/g, '&quot;')}', '${alt.replace(/['"]/g, '&quot;')}')"
              aria-label="View ${alt} sample image"
              style="width: 60px; height: 90px;">
              <img
                src="${imgSrc}"
                alt="${alt}"
                loading="lazy"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                onerror="this.style.display='none'; this.parentElement.style.opacity='0.5'">
              <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              <div class="absolute inset-0 flex items-center justify-center">
                <div class="bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-75 group-hover:scale-100">
                  <svg class="w-3 h-3 text-gray-800" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </div>
              </div>
            </button>
          `;
        }).join('')}
      </div>
    `;
  }

  /**
   * Populate text content in card
   */
  private populateTextContent(
    card: HTMLElement,
    item: ActionItem,
    problem: string,
    idea: string,
    example: string
  ): void {
    // Set recommendation text
    const recText = card.querySelector('.recommendation-text');
    if (recText) recText.textContent = item.recommendation;

    // For single-device cards
    if (item.deviceType !== 'both') {
      const problemText = card.querySelector('.problem-text');
      const ideaText = card.querySelector('.idea-text');
      const exampleText = card.querySelector('.example-text');
      const exampleContainer = card.querySelector('.example-container');

      if (problemText) problemText.textContent = problem;
      if (ideaText) ideaText.textContent = idea;

      if (example && exampleText) {
        exampleText.textContent = decodeHtmlEntities(example);
      } else if (exampleContainer) {
        (exampleContainer as HTMLElement).style.display = 'none';
      }
    }

    // For combined cards
    if (item.deviceType === 'both' && item.mobileCheck && item.desktopCheck) {
      this.populateCombinedContent(card, item);
    }
  }

  /**
   * Populate combined card content
   */
  private populateCombinedContent(card: HTMLElement, item: ActionItem): void {
    if (!item.mobileCheck || !item.desktopCheck) return;

    // Mobile section
    const mobileRec = item.mobileCheck.recommendations![0];
    const mobileDetails = mobileRec.solutionDetails;

    const mobileProblem = card.querySelector('.mobile-problem-text');
    const mobileIdea = card.querySelector('.mobile-idea-text');
    const mobileSolutionLabel = card.querySelector('.mobile-solution-label');
    const mobileExampleSection = card.querySelector('.mobile-example-section') as HTMLElement;
    const mobileExample = card.querySelector('.mobile-example-text');

    if (mobileProblem) mobileProblem.textContent = item.mobileCheck.problem || '';
    if (mobileIdea) mobileIdea.textContent = mobileDetails.idea || '';
    if (mobileSolutionLabel) {
      mobileSolutionLabel.textContent = this.getSolutionLabel(mobileDetails.actionType);
    }

    if (mobileDetails.example && mobileExample) {
      mobileExample.textContent = decodeHtmlEntities(mobileDetails.example);
    } else if (mobileExampleSection) {
      mobileExampleSection.style.display = 'none';
    }

    // Desktop section
    const desktopRec = item.desktopCheck.recommendations![0];
    const desktopDetails = desktopRec.solutionDetails;

    const desktopProblem = card.querySelector('.desktop-problem-text');
    const desktopIdea = card.querySelector('.desktop-idea-text');
    const desktopSolutionLabel = card.querySelector('.desktop-solution-label');
    const desktopExampleSection = card.querySelector('.desktop-example-section') as HTMLElement;
    const desktopExample = card.querySelector('.desktop-example-text');

    if (desktopProblem) desktopProblem.textContent = item.desktopCheck.problem || '';
    if (desktopIdea) desktopIdea.textContent = desktopDetails.idea || '';
    if (desktopSolutionLabel) {
      desktopSolutionLabel.textContent = this.getSolutionLabel(desktopDetails.actionType);
    }

    if (desktopDetails.example && desktopExample) {
      desktopExample.textContent = decodeHtmlEntities(desktopDetails.example);
    } else if (desktopExampleSection) {
      desktopExampleSection.style.display = 'none';
    }

    // Combined metrics
    this.populateCombinedMetrics(card, mobileDetails);
  }

  /**
   * Populate combined metrics section
   */
  private populateCombinedMetrics(card: HTMLElement, mobileDetails: any): void {
    const combinedLift = card.querySelector('.combined-lift-text');
    const combinedEffortLabel = card.querySelector('.combined-effort-label');
    const combinedEffortText = card.querySelector('.combined-effort-text');

    if (combinedLift) {
      combinedLift.textContent = mobileDetails.expectedLiftPct || 'N/A';
    }

    if (mobileDetails.actionType === 'test_to_quantify') {
      if (combinedEffortLabel) combinedEffortLabel.textContent = 'Required Sample';
      if (combinedEffortText) {
        combinedEffortText.textContent = this.formatSample(mobileDetails.requiredSample);
      }
    } else if (mobileDetails.actionType === 'defer') {
      if (combinedEffortLabel) combinedEffortLabel.textContent = 'Priority';
      if (combinedEffortText) {
        combinedEffortText.textContent = 'Low priority';
      }
    } else {
      const effort = mobileDetails.effort || 3;
      const effortText = EFFORT_LABELS[effort - 1] || 'Medium';
      if (combinedEffortLabel) combinedEffortLabel.textContent = 'Effort';
      if (combinedEffortText) {
        combinedEffortText.textContent = `${effortText} (${effort}/5)`;
      }
    }
  }

  /**
   * Format sample size
   */
  private formatSample(sample: number | string): string {
    const num = typeof sample === 'string' ? parseInt(sample) : sample;
    if (isNaN(num) || num === 0) return '—';
    return (Math.ceil(num / 1000) * 1000).toLocaleString();
  }

  /**
   * Get solution label based on action type
   */
  private getSolutionLabel(actionType: string): string {
    switch (actionType) {
      case 'test_to_quantify':
        return 'Test Idea:';
      case 'defer':
        return 'Suggestion:';
      default:
        return 'Implementation Idea:';
    }
  }

  /**
   * Format check title from camelCase
   */
  private static readonly ACRONYMS = new Set(['LCP','TBT','TTI','CLS','FCP','INP','CSS','JS','HTML','HTTP','ARIA','CTA','UI','SEO','AEO']);

  private formatCheckTitle(checkKey: string): string {
    if (!checkKey) return '';
    return checkKey
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => {
        const upper = word.toUpperCase();
        return CardComponent.ACRONYMS.has(upper) ? upper : word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /**
   * Find curated data for a check
   */
  private findCuratedData(checkKey: string, bucket: string, mapData: MapData | null): CuratedCheckData | null {
    if (!mapData || !mapData[bucket] || !mapData[bucket][checkKey]) {
      return null;
    }
    return mapData[bucket][checkKey];
  }

  /**
   * Check if curated data has valid references
   */
  private hasValidReferences(curatedData: CuratedCheckData | null): boolean {
    return curatedData?.references?.some(ref => ref.url && ref.url.trim() && ref.label && ref.label.trim()) || false;
  }

  /**
   * Check if curated data has valid images
   */
  private hasValidImages(curatedData: CuratedCheckData | null): boolean {
    return curatedData?.images?.some(img => img.src && img.src.trim()) || false;
  }
}
